import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link, List, ListItem, VStack } from '@chakra-ui/react';

import { KybDocumentStatus, KybDocumentType } from '__generated__/GQL';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import { createToast } from 'features/Bento/atoms';
import { CapitalDepositPartnerValue } from 'features/Onboarding/modules/CapitalDepositQuestions/pages/PartnerName/PartnerName';

import { UploadKybDocumentModal } from '../components/UploadKybDocumentModal';
import { SubmitCapitalDepositKybDocsDocument } from '../graphql/mutations/submitCapitalDepositKybDocuments.gql';
import { locales } from './locales';
import { APPLICATION_FOLLOW_UP_STEPS } from './statuses';
import { ApplicationStep, ApplicationStepCardBody } from './types';

const ApplicationRegistrationBody: ApplicationStepCardBody = ({
  data,
  stepStatus,
}) => {
  const [uploadDialogType, setUploadDialogType] = useState<
    null | KybDocumentType.Kbis | KybDocumentType.Statutes
  >(null);

  const [submitDocuments, submission] = useMutation(
    SubmitCapitalDepositKybDocsDocument,
  );

  const docs = {
    [KybDocumentType.Kbis]:
      data.kybDocuments.find(
        (document) =>
          document.type === KybDocumentType.Kbis &&
          document.status !== KybDocumentStatus.Missing,
      ) ?? null,
    [KybDocumentType.Statutes]:
      data.kybDocuments.find(
        (document) =>
          document.type === KybDocumentType.Statutes &&
          document.status !== KybDocumentStatus.Missing,
      ) ?? null,
  };

  /**
   * A list of partners for which Shine handles the dossier transfer
   * Annotated with `string` to allow checking string values
   */
  const SHINE_INTEGRATED_PARTNERS = new Set<string>([
    'Keobiz',
    'LegalPlace',
  ] satisfies CapitalDepositPartnerValue[]);

  const STATUTES_INFO_LINK =
    'https://help.shine.fr/fr/articles/6741039-j-ai-recu-mon-kbis-quelle-est-la-prochaine-etape';

  return (
    <>
      <VStack align="stretch" spacing="space-16" width="full">
        <List listStyleType="disc" paddingLeft="space-16" spacing="space-4">
          <ListItem>
            {(() => {
              if (!data.capitalDeposit.partner) {
                return (
                  <>
                    <Text>
                      {`${locales.applicationRegistration.helpSteps.sendToGuichetUnique} `}
                    </Text>

                    <Link
                      href="https://formalites.entreprises.gouv.fr/"
                      target="_blank"
                    >
                      {
                        locales.applicationRegistration.helpSteps
                          .sendToGuichetUniqueLink
                      }
                    </Link>
                  </>
                );
              }
              if (SHINE_INTEGRATED_PARTNERS.has(data.capitalDeposit.partner)) {
                return (
                  <Text>
                    {locales.formatString(
                      locales.applicationRegistration.helpSteps
                        .transferredToPartner,
                      { partner: data.capitalDeposit.partner },
                    )}
                  </Text>
                );
              }
              return (
                <Text>
                  {locales.applicationRegistration.helpSteps.transferToPartner}
                </Text>
              );
            })()}
          </ListItem>

          <ListItem>
            {data.capitalDeposit.partner &&
            SHINE_INTEGRATED_PARTNERS.has(data.capitalDeposit.partner) ? (
              <Text>
                {locales.formatString(
                  locales.applicationRegistration.helpSteps.obtainKbisByPartner,
                  { partner: data.capitalDeposit.partner },
                )}
              </Text>
            ) : (
              <Text>
                {locales.applicationRegistration.helpSteps.obtainKbis}
              </Text>
            )}
          </ListItem>
          <ListItem>
            <Text>{locales.applicationRegistration.helpSteps.uploadKbis}</Text>

            <Link
              alignSelf="flex-start"
              href={STATUTES_INFO_LINK}
              marginTop="space-8"
              target="_blank"
            >
              {locales.applicationRegistration.statutesHelpLink}
            </Link>
          </ListItem>
        </List>

        {stepStatus === 'active' ? (
          <>
            {([KybDocumentType.Kbis, KybDocumentType.Statutes] as const).map(
              (documentType) => (
                <SunshineCard
                  alignItems="center"
                  key={documentType}
                  onClick={() => setUploadDialogType(documentType)}
                  width="full"
                >
                  <SunshineCard.Slot name="aside">
                    {docs[documentType] ? (
                      <SquircleIcon backgroundColor="green.300" name="tick" />
                    ) : (
                      <SquircleIcon name="file" />
                    )}
                  </SunshineCard.Slot>

                  <SunshineCard.Content>
                    <SunshineCard.Text
                      fontSize="font-15"
                      fontWeight="weight-500"
                      variant="primary"
                    >
                      {locales.applicationRegistration[documentType].cta}
                    </SunshineCard.Text>
                  </SunshineCard.Content>

                  <SunshineCard.Slot name="action">
                    <SunshineCard.SunshineIcon name="chevron-right" />
                  </SunshineCard.Slot>
                </SunshineCard>
              ),
            )}

            {docs[KybDocumentType.Kbis] && docs[KybDocumentType.Statutes] ? (
              <Button
                isLoading={submission.loading}
                onClick={() =>
                  submitDocuments({
                    variables: {
                      input: {
                        capitalDepositId: data.capitalDeposit.capitalDepositId,
                        companyProfileId: data.companyProfile.companyProfileId,
                      },
                    },
                  })
                    .then(() => {
                      createToast({
                        status: 'success',
                        title:
                          locales.applicationRegistration.uploadSuccessToast,
                      });
                    })
                    .catch((error) => {
                      createToast({
                        status: 'error',
                        title:
                          locales.applicationRegistration.uploadFailureToast,
                      });
                      throw error;
                    })
                }
              >
                {locales.applicationRegistration.continue}
              </Button>
            ) : null}
          </>
        ) : null}
      </VStack>

      <UploadKybDocumentModal
        data={data}
        documentType={uploadDialogType}
        existingDocument={uploadDialogType ? docs[uploadDialogType] : null}
        onClose={() => setUploadDialogType(null)}
      />
    </>
  );
};

export const applicationRegistrationStep = {
  activeBadgeLabel: locales.activeBadgeLabel.todo,

  body: ApplicationRegistrationBody,

  isActive: ({ capitalDeposit }) =>
    APPLICATION_FOLLOW_UP_STEPS[capitalDeposit.status] === 'registration',

  title: locales.applicationRegistration.title,
} satisfies ApplicationStep;
